import React, { useState, useEffect } from 'react';
import { Table, Card, Button, message, Tag, Space, Modal, Form, Input, Select, DatePicker, InputNumber, Popconfirm, Descriptions } from 'antd';
import { PlusOutlined, PrinterOutlined, EditOutlined, EyeOutlined, DeleteOutlined, DollarOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import i18n from '../../i18n';
import { paymentService, patientService } from '../../services';
import { useApp } from '../../context/AppContext';
import dayjs from 'dayjs';
import { formatCurrency } from '../../utils/currency';
import api from '../../services/api';
import { getTextDirection } from '../../utils/printUtils';

const Payments = () => {
    const { t } = useTranslation(['payment', 'common']);
    const { user } = useApp();
    const isDentist = user?.role === 'dentist';
    const [payments, setPayments] = useState([]);
    const [patients, setPatients] = useState([]);
    const [dentists, setDentists] = useState([]);
    const [treatments, setTreatments] = useState([]);
    const [loading, setLoading] = useState(false);
    const [createModalVisible, setCreateModalVisible] = useState(false);
    const [editModalVisible, setEditModalVisible] = useState(false);
    const [viewModalVisible, setViewModalVisible] = useState(false);
    const [recordPaymentModalVisible, setRecordPaymentModalVisible] = useState(false);
    const [selectedPayment, setSelectedPayment] = useState(null);
    const [form] = Form.useForm();
    const [recordPaymentForm] = Form.useForm();
    const [createCurrency, setCreateCurrency] = useState('USD');
    const [editCurrency, setEditCurrency] = useState('USD');

    // Filter states
    const [filters, setFilters] = useState({
        search: '',
        dentistId: null,
        paymentStatus: null,
        dateRange: [null, null]
    });

    useEffect(() => {
        fetchPayments();
        fetchPatients();
        fetchDentists();
    }, [filters]);

    const fetchDentists = async () => {
        try {
            const response = await api.get('/dentists', { params: { limit: 1000, isActive: true } });
            if (response.data.success) {
                setDentists(response.data.data);
            }
        } catch (error) {
            console.error('Failed to fetch dentists:', error);
            message.error(t('common:errors.fetchFailed', 'Failed to fetch dentists'));
        }
    };

    const fetchPayments = async () => {
        setLoading(true);
        try {
            const params = {};

            if (filters.search) params.search = filters.search;
            if (filters.dentistId) params.dentistId = filters.dentistId;
            if (filters.paymentStatus) params.paymentStatus = filters.paymentStatus;
            if (filters.dateRange[0] && filters.dateRange[1]) {
                params.startDate = filters.dateRange[0].toISOString();
                params.endDate = filters.dateRange[1].toISOString();
            }

            const response = await paymentService.getAll(params);
            if (response.success) {
                setPayments(response.data);
            }
        } catch (error) {
            message.error(t('payment:messages.fetchFailed'));
        } finally {
            setLoading(false);
        }
    };

    const fetchPatients = async () => {
        try {
            const response = await patientService.getAll({});
            if (response.success) {
                setPatients(response.data);
            }
        } catch (error) {
            console.error('Failed to fetch patients:', error);
        }
    };

    const handleCreateInvoice = () => {
        form.resetFields();
        setCreateCurrency('USD');
        setCreateModalVisible(true);
    };

    const handleEditPayment = (payment) => {
        setSelectedPayment(payment);
        setEditCurrency(payment.currency || 'USD');
        form.setFieldsValue({
            ...payment,
            date: dayjs(payment.date),
            patientId: payment.patientId?._id
        });
        setEditModalVisible(true);
    };

    const handleViewPayment = async (payment) => {
        try {
            const response = await paymentService.getOne(payment._id);
            if (response.success) {
                setSelectedPayment(response.data);
                setViewModalVisible(true);
            }
        } catch (error) {
            message.error(t('payment:messages.fetchFailed'));
        }
    };

    const handleRecordPayment = (payment) => {
        setSelectedPayment(payment);
        recordPaymentForm.resetFields();
        recordPaymentForm.setFieldsValue({
            paymentMethod: payment.paymentMethod
        });
        setRecordPaymentModalVisible(true);
    };

    const handleDeletePayment = async (id) => {
        try {
            const response = await paymentService.delete(id);
            if (response.success) {
                message.success(t('payment:messages.deleteSuccess'));
                fetchPayments();
            }
        } catch (error) {
            message.error(t('payment:messages.deleteFailed'));
        }
    };

    const handleCreateSubmit = async (values) => {
        try {
            const response = await paymentService.create({
                ...values,
                date: values.date?.toISOString()
            });
            if (response.success) {
                message.success(t('payment:messages.createSuccess'));
                setCreateModalVisible(false);
                fetchPayments();
                form.resetFields();
            }
        } catch (error) {
            message.error(t('payment:messages.createFailed'));
        }
    };

    const handleEditSubmit = async (values) => {
        try {
            const response = await paymentService.update(selectedPayment._id, {
                ...values,
                date: values.date?.toISOString()
            });
            if (response.success) {
                message.success(t('payment:messages.updateSuccess'));
                setEditModalVisible(false);
                fetchPayments();
            }
        } catch (error) {
            message.error(t('payment:messages.updateFailed'));
        }
    };

    const handleRecordPaymentSubmit = async (values) => {
        try {
            const response = await paymentService.recordPayment(selectedPayment._id, values);
            if (response.success) {
                message.success(t('payment:messages.recordSuccess'));
                setRecordPaymentModalVisible(false);
                fetchPayments();
            }
        } catch (error) {
            message.error(t('payment:messages.recordFailed'));
        }
    };

    const handlePrint = async (payment) => {
        try {
            const locale = i18n.language;
            const direction = getTextDirection(locale);
            const response = await api.get(`/payments/${payment._id}/print?lang=${locale}`);

            if (response.data.success) {
                const printWindow = window.open('', '_blank');
                if (!printWindow) {
                    message.error(t('common:printBlocked') || 'Please allow popups to print invoices');
                    return;
                }

                const htmlContent = `
                    <!DOCTYPE html>
                    <html dir="${direction}">
                    <head>
                        <meta charset="UTF-8">
                        <meta name="viewport" content="width=device-width, initial-scale=1.0">
                        <title>${t('print:invoice') || 'Invoice'} ${payment.invoiceNumber}</title>
                        <link rel="stylesheet" href="/styles/print.css">
                        <style>
                            * { margin: 0; padding: 0; box-sizing: border-box; }
                            body { font-family: ${direction === 'rtl' ? "'Segoe UI', 'Arial Unicode MS'" : "'Segoe UI', Arial"}, sans-serif; padding: 30px; }
                        </style>
                    </head>
                    <body>
                        ${response.data.data.html}
                    </body>
                    </html>
                `;

                printWindow.document.write(htmlContent);
                printWindow.document.close();
                printWindow.focus();
                setTimeout(() => {
                    printWindow.print();
                }, 300);

                message.success(t('print:printSuccess') || 'Document sent to printer');
            }
        } catch (error) {
            message.error(t('print:printError') || 'Failed to load invoice for printing');
            console.error('Print error:', error);
        }
    };

    const statusColors = {
        Pending: 'orange',
        Partial: 'blue',
        Paid: 'green',
        Refunded: 'purple',
        Cancelled: 'red'
    };

    const columns = [
        {
            title: t('payment:invoiceNumber'),
            dataIndex: 'invoiceNumber',
            key: 'invoiceNumber'
        },
        {
            title: t('payment:patient'),
            dataIndex: ['patientId', 'name'],
            key: 'patient'
        },
        {
            title: t('payment:date'),
            dataIndex: 'date',
            key: 'date',
            render: (date) => dayjs(date).format('MMM DD, YYYY')
        },
        {
            title: t('payment:totalAmount'),
            dataIndex: 'totalAmount',
            key: 'totalAmount',
            render: (amount, record) => formatCurrency(amount, record.currency || 'USD')
        },
        {
            title: t('payment:amountPaid'),
            dataIndex: 'amountPaid',
            key: 'amountPaid',
            render: (amount, record) => formatCurrency(amount, record.currency || 'USD')
        },
        {
            title: t('payment:balanceDue'),
            dataIndex: 'balanceDue',
            key: 'balanceDue',
            render: (amount, record) => formatCurrency(amount, record.currency || 'USD')
        },
        {
            title: t('payment:paymentMethod'),
            dataIndex: 'paymentMethod',
            key: 'paymentMethod'
        },
        {
            title: t('payment:status'),
            dataIndex: 'paymentStatus',
            key: 'paymentStatus',
            render: (status) => <Tag color={statusColors[status]}>{status}</Tag>
        },
        {
            title: t('common:actions'),
            key: 'actions',
            render: (_, record) => (
                <Space>
                    <Button
                        type="link"
                        icon={<EyeOutlined />}
                        onClick={() => handleViewPayment(record)}
                    >
                        {t('common:view')}
                    </Button>
                    <Button
                        type="link"
                        icon={<EditOutlined />}
                        onClick={() => handleEditPayment(record)}
                        disabled={record.paymentStatus === 'Cancelled'}
                    >
                        {t('common:edit')}
                    </Button>
                    <Button
                        type="link"
                        icon={<DollarOutlined />}
                        onClick={() => handleRecordPayment(record)}
                        disabled={record.paymentStatus === 'Paid' || record.paymentStatus === 'Cancelled'}
                    >
                        {t('payment:recordPayment')}
                    </Button>
                    <Button
                        type="link"
                        icon={<PrinterOutlined />}
                        onClick={() => handlePrint(record)}
                    >
                        {t('payment:print')}
                    </Button>
                    <Popconfirm
                        title={t('payment:messages.deleteConfirm')}
                        onConfirm={() => handleDeletePayment(record._id)}
                        okText={t('common:yes')}
                        cancelText={t('common:no')}
                    >
                        <Button
                            type="link"
                            danger
                            icon={<DeleteOutlined />}
                            disabled={record.paymentStatus === 'Cancelled'}
                        >
                            {t('common:delete')}
                        </Button>
                    </Popconfirm>
                </Space>
            )
        }
    ];

    return (
        <Card>
            <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between' }}>
                <h1>{t('payment:title')}</h1>
                <Button type="primary" icon={<PlusOutlined />} onClick={handleCreateInvoice}>
                    {t('payment:createInvoice')}
                </Button>
            </div>

            {/* Filters */}
            <Card style={{ marginBottom: 16, backgroundColor: '#fafafa' }} size="small">
                <Space direction="vertical" style={{ width: '100%' }} size="middle">
                    <Space wrap>
                        <Input.Search
                            placeholder="Search by invoice, patient name, description..."
                            allowClear
                            style={{ width: 300 }}
                            onSearch={(value) => setFilters(prev => ({ ...prev, search: value }))}
                            onChange={(e) => !e.target.value && setFilters(prev => ({ ...prev, search: '' }))}
                        />
                        {!isDentist && (
                            <Select
                                style={{ width: 200 }}
                                placeholder="Select Dentist"
                                allowClear
                                onChange={(value) => setFilters(prev => ({ ...prev, dentistId: value }))}
                                showSearch
                                optionFilterProp="children"
                            >
                                {dentists.map(dentist => (
                                    <Select.Option key={dentist._id} value={dentist._id}>
                                        {dentist.name}
                                    </Select.Option>
                                ))}
                            </Select>
                        )}
                        <Select
                            style={{ width: 150 }}
                            placeholder="Payment Status"
                            allowClear
                            onChange={(value) => setFilters(prev => ({ ...prev, paymentStatus: value }))}
                        >
                            <Select.Option value="Paid">Paid</Select.Option>
                            <Select.Option value="Pending">Pending</Select.Option>
                            <Select.Option value="Partial">Partial</Select.Option>
                            <Select.Option value="Cancelled">Cancelled</Select.Option>
                        </Select>
                        <DatePicker.RangePicker
                            onChange={(dates) => setFilters(prev => ({ ...prev, dateRange: dates || [null, null] }))}
                            format="YYYY-MM-DD"
                        />
                        <Button
                            onClick={() => setFilters({ search: '', dentistId: null, paymentStatus: null, dateRange: [null, null] })}
                        >
                            Clear Filters
                        </Button>
                    </Space>
                </Space>
            </Card>

            <Table
                columns={columns}
                dataSource={payments}
                rowKey="_id"
                loading={loading}
                scroll={{ x: 'max-content' }}
            />

            {/* Create Invoice Modal */}
            <Modal
                title={t('payment:createInvoice')}
                open={createModalVisible}
                onCancel={() => setCreateModalVisible(false)}
                onOk={() => form.submit()}
                width={600}
            >
                <Form form={form} layout="vertical" onFinish={handleCreateSubmit}>
                    <Form.Item
                        name="patientId"
                        label={t('payment:patient')}
                        rules={[{ required: true, message: t('payment:messages.patientRequired') }]}
                    >
                        <Select
                            showSearch
                            placeholder={t('payment:selectPatient')}
                            optionFilterProp="children"
                        >
                            {patients.map(patient => (
                                <Select.Option key={patient._id} value={patient._id}>
                                    {patient.name}
                                </Select.Option>
                            ))}
                        </Select>
                    </Form.Item>
                    {!isDentist && (
                        <Form.Item
                            name="dentistId"
                            label={t('payment:dentist', 'Dentist')}
                            rules={[{ required: true, message: 'Please select a dentist' }]}
                        >
                            <Select
                                showSearch
                                placeholder="Select dentist"
                                optionFilterProp="children"
                            >
                                {dentists.map(dentist => (
                                    <Select.Option key={dentist._id} value={dentist._id}>
                                        {dentist.name} - {dentist.specialization}
                                    </Select.Option>
                                ))}
                            </Select>
                        </Form.Item>
                    )}
                    <Form.Item
                        name="currency"
                        label={t('payment:currency')}
                        initialValue="USD"
                        rules={[{ required: true }]}
                    >
                        <Select onChange={(val) => setCreateCurrency(val)}>
                            <Select.Option value="USD">$ USD</Select.Option>
                            <Select.Option value="IQD">IQD - دینار عراقی</Select.Option>
                        </Select>
                    </Form.Item>
                    <Form.Item
                        name="date"
                        label={t('payment:date')}
                        rules={[{ required: true, message: t('payment:messages.dateRequired') }]}
                    >
                        <DatePicker style={{ width: '100%' }} />
                    </Form.Item>
                    <Form.Item
                        name="totalAmount"
                        label={t('payment:totalAmount')}
                        rules={[{ required: true, message: t('payment:messages.amountRequired') }]}
                    >
                        <InputNumber min={0} style={{ width: '100%' }} prefix={createCurrency === 'IQD' ? 'IQD' : '$'} />
                    </Form.Item>
                    <Form.Item
                        name="amountPaid"
                        label={t('payment:amountPaid')}
                        initialValue={0}
                    >
                        <InputNumber min={0} style={{ width: '100%' }} prefix={createCurrency === 'IQD' ? 'IQD' : '$'} />
                    </Form.Item>
                    <Form.Item
                        name="paymentMethod"
                        label={t('payment:paymentMethod')}
                        rules={[{ required: true, message: t('payment:messages.methodRequired') }]}
                    >
                        <Select>
                            <Select.Option value="Cash">{t('payment:cash')}</Select.Option>
                            <Select.Option value="Credit Card">{t('payment:creditCard')}</Select.Option>
                            <Select.Option value="Debit Card">{t('payment:debitCard')}</Select.Option>
                            <Select.Option value="Bank Transfer">{t('payment:bankTransfer')}</Select.Option>
                        </Select>
                    </Form.Item>
                    <Form.Item name="description" label={t('payment:description')}>
                        <Input.TextArea rows={3} />
                    </Form.Item>
                    <Form.Item name="notes" label={t('payment:notes')}>
                        <Input.TextArea rows={2} />
                    </Form.Item>
                </Form>
            </Modal>

            {/* Edit Payment Modal */}
            <Modal
                title={t('payment:editPayment')}
                open={editModalVisible}
                onCancel={() => setEditModalVisible(false)}
                onOk={() => form.submit()}
                width={600}
            >
                <Form form={form} layout="vertical" onFinish={handleEditSubmit}>
                    <Form.Item
                        name="patientId"
                        label={t('payment:patient')}
                        rules={[{ required: true, message: t('payment:messages.patientRequired') }]}
                    >
                        <Select
                            showSearch
                            placeholder={t('payment:selectPatient')}
                            optionFilterProp="children"
                        >
                            {patients.map(patient => (
                                <Select.Option key={patient._id} value={patient._id}>
                                    {patient.name}
                                </Select.Option>
                            ))}
                        </Select>
                    </Form.Item>
                    <Form.Item
                        name="currency"
                        label={t('payment:currency')}
                        rules={[{ required: true }]}
                    >
                        <Select onChange={(val) => setEditCurrency(val)}>
                            <Select.Option value="USD">$ USD</Select.Option>
                            <Select.Option value="IQD">IQD - دینار عراقی</Select.Option>
                        </Select>
                    </Form.Item>
                    <Form.Item
                        name="date"
                        label={t('payment:date')}
                        rules={[{ required: true, message: t('payment:messages.dateRequired') }]}
                    >
                        <DatePicker style={{ width: '100%' }} />
                    </Form.Item>
                    <Form.Item
                        name="totalAmount"
                        label={t('payment:totalAmount')}
                        rules={[{ required: true, message: t('payment:messages.amountRequired') }]}
                    >
                        <InputNumber min={0} style={{ width: '100%' }} prefix={editCurrency === 'IQD' ? 'IQD' : '$'} />
                    </Form.Item>
                    <Form.Item
                        name="paymentMethod"
                        label={t('payment:paymentMethod')}
                        rules={[{ required: true, message: t('payment:messages.methodRequired') }]}
                    >
                        <Select>
                            <Select.Option value="Cash">{t('payment:cash')}</Select.Option>
                            <Select.Option value="Credit Card">{t('payment:creditCard')}</Select.Option>
                            <Select.Option value="Debit Card">{t('payment:debitCard')}</Select.Option>
                            <Select.Option value="Bank Transfer">{t('payment:bankTransfer')}</Select.Option>
                        </Select>
                    </Form.Item>
                    <Form.Item name="description" label={t('payment:description')}>
                        <Input.TextArea rows={3} />
                    </Form.Item>
                    <Form.Item name="notes" label={t('payment:notes')}>
                        <Input.TextArea rows={2} />
                    </Form.Item>
                </Form>
            </Modal>

            {/* View Payment Modal */}
            <Modal
                title={t('payment:viewPayment')}
                open={viewModalVisible}
                onCancel={() => setViewModalVisible(false)}
                footer={[
                    <Button key="close" onClick={() => setViewModalVisible(false)}>
                        {t('common:close')}
                    </Button>,
                    <Button key="print" type="primary" icon={<PrinterOutlined />} onClick={() => handlePrint(selectedPayment)}>
                        {t('payment:print')}
                    </Button>
                ]}
                width={700}
            >
                {selectedPayment && (
                    <Descriptions bordered column={2}>
                        <Descriptions.Item label={t('payment:invoiceNumber')} span={2}>
                            {selectedPayment.invoiceNumber}
                        </Descriptions.Item>
                        <Descriptions.Item label={t('payment:patient')} span={2}>
                            {selectedPayment.patientId?.name}
                        </Descriptions.Item>
                        <Descriptions.Item label={t('payment:date')}>
                            {dayjs(selectedPayment.date).format('MMM DD, YYYY')}
                        </Descriptions.Item>
                        <Descriptions.Item label={t('payment:status')}>
                            <Tag color={statusColors[selectedPayment.paymentStatus]}>
                                {selectedPayment.paymentStatus}
                            </Tag>
                        </Descriptions.Item>
                        <Descriptions.Item label={t('payment:currency')}>
                            {selectedPayment.currency || 'USD'}
                        </Descriptions.Item>
                        <Descriptions.Item label={t('payment:totalAmount')}>
                            {formatCurrency(selectedPayment.totalAmount, selectedPayment.currency || 'USD')}
                        </Descriptions.Item>
                        <Descriptions.Item label={t('payment:amountPaid')}>
                            {formatCurrency(selectedPayment.amountPaid, selectedPayment.currency || 'USD')}
                        </Descriptions.Item>
                        <Descriptions.Item label={t('payment:balanceDue')}>
                            {formatCurrency(selectedPayment.balanceDue, selectedPayment.currency || 'USD')}
                        </Descriptions.Item>
                        <Descriptions.Item label={t('payment:paymentMethod')}>
                            {selectedPayment.paymentMethod}
                        </Descriptions.Item>
                        {selectedPayment.description && (
                            <Descriptions.Item label={t('payment:description')} span={2}>
                                {selectedPayment.description}
                            </Descriptions.Item>
                        )}
                        {selectedPayment.notes && (
                            <Descriptions.Item label={t('payment:notes')} span={2}>
                                {selectedPayment.notes}
                            </Descriptions.Item>
                        )}
                    </Descriptions>
                )}
            </Modal>

            {/* Record Payment Modal */}
            <Modal
                title={t('payment:recordPayment')}
                open={recordPaymentModalVisible}
                onCancel={() => setRecordPaymentModalVisible(false)}
                onOk={() => recordPaymentForm.submit()}
            >
                <Form form={recordPaymentForm} layout="vertical" onFinish={handleRecordPaymentSubmit}>
                    <Form.Item
                        name="amount"
                        label={t('payment:amount')}
                        rules={[{ required: true, message: t('payment:messages.amountRequired') }]}
                    >
                        <InputNumber
                            min={0}
                            max={selectedPayment?.balanceDue}
                            style={{ width: '100%' }}
                            prefix={selectedPayment?.currency === 'IQD' ? 'IQD' : '$'}
                        />
                    </Form.Item>
                    <Form.Item
                        name="paymentMethod"
                        label={t('payment:paymentMethod')}
                        rules={[{ required: true, message: t('payment:messages.methodRequired') }]}
                    >
                        <Select>
                            <Select.Option value="Cash">{t('payment:cash')}</Select.Option>
                            <Select.Option value="Credit Card">{t('payment:creditCard')}</Select.Option>
                            <Select.Option value="Debit Card">{t('payment:debitCard')}</Select.Option>
                            <Select.Option value="Bank Transfer">{t('payment:bankTransfer')}</Select.Option>
                        </Select>
                    </Form.Item>
                    <Form.Item name="transactionId" label={t('payment:transactionId')}>
                        <Input />
                    </Form.Item>
                    <Form.Item name="notes" label={t('payment:notes')}>
                        <Input.TextArea rows={3} />
                    </Form.Item>
                </Form>
            </Modal>
        </Card>
    );
};

export default Payments;
